import { Tabs, TabsContent, TabsList, TabsTrigger, Button, Label } from '@creanizer/ui'
import { GenerateButton } from '@creanizer/ui'
import type { WorldBuilderInput, WorldBuilderResult, WorldBuilderDetailedLockedSections } from '@creanizer/types'
import { Lock, Unlock } from 'lucide-react'
import { PresetModeForm } from './PresetModeForm'
import { StandardModeForm } from './StandardModeForm'
import { TemplateModeForm } from './TemplateModeForm'
import { RandomModeForm } from './RandomModeForm'
import { IdeaModeForm } from './IdeaModeForm'
import { ReferenceModeForm } from './ReferenceModeForm'
import { WorldParametersForm } from './WorldParametersForm'
import { UniqueElementsForm } from './UniqueElementsForm'
import { GenerationOptionsForm } from './GenerationOptionsForm'

interface WorldBuilderFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
  onGenerate: () => void
  isGenerating: boolean
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
  partialRegenerateMode?: boolean
  existingResult?: WorldBuilderResult | null
  lockedSections?: WorldBuilderDetailedLockedSections
  onLockedSectionsChange?: (sections: WorldBuilderDetailedLockedSections) => void
  onPartialRegenerateModeChange?: (mode: boolean) => void
}

export function WorldBuilderForm({
  input,
  onChange,
  onGenerate,
  isGenerating,
  useStreaming = false,
  onStreamingChange,
  partialRegenerateMode = false,
  existingResult = null,
  lockedSections = {},
  onLockedSectionsChange,
  onPartialRegenerateModeChange,
}: WorldBuilderFormProps) {
  // モード変更時のハンドラ - 他のモードの入力をクリア
  const handleModeChange = (newMode: string) => {
    if (newMode === 'preset') {
      onChange({
        mode: 'preset',
        presetType: 'modern-world',
        presetCustomizations: [], // プリセットカスタマイズも初期化
        parameters: undefined, // プリセットが自動設定
        creativityLevel: input.creativityLevel || 7,
        detailLevel: input.detailLevel || 5,
        includeTimeline: input.includeTimeline ?? true,
        includeCultures: input.includeCultures ?? true,
        includeGeography: input.includeGeography ?? true,
        seed: input.seed,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'standard') {
      onChange({
        mode: 'standard',
        worldName: '',
        concept: '',
        keywords: [],
        genres: [],
        worldScale: 'country',
        parameters: {
          magicLevel: 5,
          scienceLevel: 5,
          civilizationLevel: 5,
          dangerLevel: 5,
          mysteryLevel: 5,
          complexityLevel: 5,
          uniquenessLevel: 5,
          chaosLevel: 3,
        },
        creativityLevel: input.creativityLevel || 7,
        detailLevel: input.detailLevel || 5,
        includeTimeline: input.includeTimeline ?? true,
        includeCultures: input.includeCultures ?? true,
        includeGeography: input.includeGeography ?? true,
        seed: input.seed,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'template') {
      onChange({
        mode: 'template',
        templateType: undefined,
        templateModifiers: [],
        parameters: {
          magicLevel: 5,
          scienceLevel: 5,
          civilizationLevel: 5,
          dangerLevel: 5,
          mysteryLevel: 5,
          complexityLevel: 5,
          uniquenessLevel: 5,
          chaosLevel: 3,
        },
        creativityLevel: input.creativityLevel || 7,
        detailLevel: input.detailLevel || 5,
        includeTimeline: input.includeTimeline ?? true,
        includeCultures: input.includeCultures ?? true,
        includeGeography: input.includeGeography ?? true,
        seed: input.seed,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'random') {
      onChange({
        mode: 'random',
        randomConstraints: {},
        parameters: undefined, // ランダムモードで自動生成
        creativityLevel: input.creativityLevel || 9,
        detailLevel: input.detailLevel || 5,
        includeTimeline: input.includeTimeline ?? true,
        includeCultures: input.includeCultures ?? true,
        includeGeography: input.includeGeography ?? true,
        seed: input.seed,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'idea') {
      onChange({
        mode: 'idea',
        ideaText: '',
        ideaFile: undefined,
        parameters: undefined, // LLMが自動決定
        creativityLevel: input.creativityLevel || 7,
        detailLevel: input.detailLevel || 5,
        includeTimeline: input.includeTimeline ?? true,
        includeCultures: input.includeCultures ?? true,
        includeGeography: input.includeGeography ?? true,
        seed: input.seed,
        useProMode: input.useProMode,
      })
    } else if (newMode === 'reference') {
      onChange({
        mode: 'reference',
        references: {},
        referenceContext: '',
        parameters: undefined, // LLMが自動決定
        creativityLevel: input.creativityLevel || 7,
        detailLevel: input.detailLevel || 5,
        includeTimeline: input.includeTimeline ?? true,
        includeCultures: input.includeCultures ?? true,
        includeGeography: input.includeGeography ?? true,
        seed: input.seed,
        useProMode: input.useProMode,
      })
    }
  }
  
  return (
    <div className="space-y-6">
      {/* 部分再生成モードの表示 */}
      {partialRegenerateMode && existingResult && (
        <div className="card bg-muted/50 border-2 border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">部分再生成モード</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (onPartialRegenerateModeChange) {
                  onPartialRegenerateModeChange(false)
                }
              }}
            >
              通常モードに戻る
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            読み込んだ世界観「{existingResult.name}」の部分的な再生成を行います。
            ロックしたセクションは保持され、ロックされていない部分のみ再生成されます。
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>再生成する項目を選択（ロック = 保持、アンロック = 再生成）:</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (onLockedSectionsChange) {
                      // 基本情報（変更不可）以外をすべてアンロック
                      onLockedSectionsChange({
                        // 基本情報は常にロック
                        name: true,
                        concept: true,
                        worldScale: true,
                        genres: true
                      })
                    }
                  }}
                >
                  すべてアンロック（再生成）
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (onLockedSectionsChange && existingResult) {
                      // すべての項目をロック
                      onLockedSectionsChange({
                        // 基本情報（常にロック）
                        name: true,
                        concept: true,
                        worldScale: true,
                        genres: true,
                        summary: true,
                        
                        // パラメータ
                        parameters: {
                          magicLevel: true,
                          scienceLevel: true,
                          civilizationLevel: true,
                          dangerLevel: true,
                          mysteryLevel: true,
                          complexityLevel: true,
                          uniquenessLevel: true,
                          chaosLevel: true
                        },
                        
                        // 独自要素
                        uniqueFeatures: {
                          signature: true,
                          laws: true,
                          paradoxes: true,
                          mysteries: true,
                          innovations: true
                        },
                        
                        // 詳細
                        details: {
                          era: {
                            name: true,
                            description: true,
                            technologyLevel: true,
                            socialStructure: true
                          },
                          geography: true,
                          cultures: true,
                          timeline: true,
                          magicSystem: true,
                          technology: true,
                          threats: {
                            hostileForces: true,
                            conflictSituation: true,
                            worldDangers: true
                          }
                        },
                        
                        // 整合性
                        consistency: true
                      })
                    }
                  }}
                >
                  すべてロック（保持）
                </Button>
              </div>
            </div>
            
            {/* タブで項目をグループ化 */}
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-1 w-full">
                <TabsTrigger value="basic">基本</TabsTrigger>
                <TabsTrigger value="parameters">パラメータ</TabsTrigger>
                <TabsTrigger value="unique">独自要素</TabsTrigger>
                <TabsTrigger value="culture">歴史・文化</TabsTrigger>
                <TabsTrigger value="world">世界設定</TabsTrigger>
                <TabsTrigger value="systems">システム</TabsTrigger>
              </TabsList>
              
              {/* 基本タブ */}
              <TabsContent value="basic" className="space-y-4">
                <div className="space-y-3">
                  <div className="text-sm font-medium">基本情報（変更不可）</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { key: 'name', label: '世界名', immutable: true },
                      { key: 'concept', label: 'コンセプト', immutable: true },
                      { key: 'worldScale', label: '世界規模', immutable: true },
                      { key: 'genres', label: 'ジャンル', immutable: true }
                    ].map(item => (
                      <Button
                        key={item.key}
                        variant="default"
                        size="sm"
                        disabled
                        className="justify-start opacity-60 cursor-not-allowed"
                      >
                        <Lock className="h-3 w-3 mr-1" />
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium">変更可能な基本情報</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { key: 'summary', label: '概要' }
                    ].map(item => {
                      const isLocked = lockedSections[item.key as keyof WorldBuilderDetailedLockedSections]
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              onLockedSectionsChange({
                                ...lockedSections,
                                [item.key]: !isLocked
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium">時代設定</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { key: 'name', label: '時代名', path: ['details', 'era'] },
                      { key: 'description', label: '時代説明', path: ['details', 'era'] },
                      { key: 'technologyLevel', label: '技術レベル', path: ['details', 'era'] },
                      { key: 'socialStructure', label: '社会構造', path: ['details', 'era'] }
                    ].map(item => {
                      const isLocked = typeof lockedSections.details?.era === 'object' 
                        ? lockedSections.details.era[item.key as keyof typeof lockedSections.details.era]
                        : lockedSections.details?.era
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentEra = typeof lockedSections.details?.era === 'object' 
                                ? lockedSections.details.era 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                details: {
                                  ...lockedSections.details,
                                  era: {
                                    ...currentEra,
                                    [item.key]: !isLocked
                                  }
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium">脅威と危険</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { key: 'hostileForces', label: '敵性存在' },
                      { key: 'conflictSituation', label: '世界情勢と紛争' },
                      { key: 'worldDangers', label: '潜在的な世界の危機' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.details?.threats === 'object' 
                        ? lockedSections.details.threats[item.key as keyof typeof lockedSections.details.threats]
                        : lockedSections.details?.threats
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentThreats = typeof lockedSections.details?.threats === 'object' 
                                ? lockedSections.details.threats 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                details: {
                                  ...lockedSections.details,
                                  threats: {
                                    ...currentThreats,
                                    [item.key]: !isLocked
                                  }
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              </TabsContent>
              
              {/* パラメータタブ（部分生成不要） */}
              <TabsContent value="parameters" className="space-y-2">
                <div className="text-sm text-muted-foreground p-4 bg-muted/50 rounded">
                  パラメータは世界観の基礎となる数値のため、部分再生成の対象外です。
                  全体を再生成する場合はすべてのパラメータが更新されます。
                </div>
              </TabsContent>
              
              {/* 独自要素タブ */}
              <TabsContent value="unique" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'signature', label: 'シグネチャー' },
                    { key: 'laws', label: '法則・ルール' },
                    { key: 'paradoxes', label: 'パラドックス・矛盾' },
                    { key: 'mysteries', label: '未解明の謎' },
                    { key: 'innovations', label: '独自の発明・発見' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.uniqueFeatures === 'object' 
                      ? lockedSections.uniqueFeatures[item.key as keyof typeof lockedSections.uniqueFeatures]
                      : lockedSections.uniqueFeatures
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentFeatures = typeof lockedSections.uniqueFeatures === 'object' 
                              ? lockedSections.uniqueFeatures 
                              : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              uniqueFeatures: {
                                ...currentFeatures,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>
              
              {/* 歴史・文化タブ */}
              <TabsContent value="culture" className="space-y-4">
                <div className="space-y-3">
                  <div className="text-sm font-medium">歴史年表</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Button
                      variant={lockedSections.details?.timeline === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (onLockedSectionsChange) {
                          onLockedSectionsChange({
                            ...lockedSections,
                            details: {
                              ...lockedSections.details,
                              timeline: lockedSections.details?.timeline === true ? false : true
                            }
                          })
                        }
                      }}
                      className="justify-start"
                    >
                      {lockedSections.details?.timeline === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                      タイムライン全体
                    </Button>
                  </div>
                  
                  {existingResult?.details?.timeline && existingResult.details.timeline.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">各イベントを個別に選択:</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {existingResult.details.timeline.map((event, index) => {
                          const isLocked = Array.isArray(lockedSections.details?.timeline) 
                            ? lockedSections.details.timeline.includes(index)
                            : lockedSections.details?.timeline === true
                          return (
                            <Button
                              key={index}
                              variant={isLocked ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                if (onLockedSectionsChange) {
                                  let newTimeline: boolean | number[]
                                  if (Array.isArray(lockedSections.details?.timeline)) {
                                    const current = lockedSections.details.timeline
                                    if (current.includes(index)) {
                                      newTimeline = current.filter(i => i !== index)
                                    } else {
                                      newTimeline = [...current, index]
                                    }
                                  } else {
                                    newTimeline = isLocked ? [] : [index]
                                  }
                                  onLockedSectionsChange({
                                    ...lockedSections,
                                    details: {
                                      ...lockedSections.details,
                                      timeline: newTimeline.length === 0 ? false : newTimeline
                                    }
                                  })
                                }
                              }}
                              className="justify-start text-left"
                            >
                              {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                              <span className="truncate">{index + 1}. {event.year || 'イベント'}</span>
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium">文化圏</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Button
                      variant={lockedSections.details?.cultures === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (onLockedSectionsChange) {
                          onLockedSectionsChange({
                            ...lockedSections,
                            details: {
                              ...lockedSections.details,
                              cultures: lockedSections.details?.cultures === true ? false : true
                            }
                          })
                        }
                      }}
                      className="justify-start"
                    >
                      {lockedSections.details?.cultures === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                      文化圏全体
                    </Button>
                  </div>
                  
                  {existingResult?.details?.cultures && existingResult.details.cultures.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">各文化を個別に選択:</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {existingResult.details.cultures.map((culture, index) => {
                          const isLocked = Array.isArray(lockedSections.details?.cultures) 
                            ? lockedSections.details.cultures.includes(index)
                            : lockedSections.details?.cultures === true
                          return (
                            <Button
                              key={index}
                              variant={isLocked ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                if (onLockedSectionsChange) {
                                  let newCultures: boolean | number[]
                                  if (Array.isArray(lockedSections.details?.cultures)) {
                                    const current = lockedSections.details.cultures
                                    if (current.includes(index)) {
                                      newCultures = current.filter(i => i !== index)
                                    } else {
                                      newCultures = [...current, index]
                                    }
                                  } else {
                                    newCultures = isLocked ? [] : [index]
                                  }
                                  onLockedSectionsChange({
                                    ...lockedSections,
                                    details: {
                                      ...lockedSections.details,
                                      cultures: newCultures.length === 0 ? false : newCultures
                                    }
                                  })
                                }
                              }}
                              className="justify-start text-left"
                            >
                              {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                              <span className="truncate">{culture.name || `文化${index + 1}`}</span>
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* 世界設定タブ */}
              <TabsContent value="world" className="space-y-4">
                {/* 地理 */}
                <div className="space-y-3">
                  <div className="text-sm font-medium">地理</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Button
                      variant={lockedSections.details?.geography === true ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (onLockedSectionsChange) {
                          onLockedSectionsChange({
                            ...lockedSections,
                            details: {
                              ...lockedSections.details,
                              geography: lockedSections.details?.geography === true ? false : true
                            }
                          })
                        }
                      }}
                      className="justify-start"
                    >
                      {lockedSections.details?.geography === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                      地理全体
                    </Button>
                  </div>
                  
                  {/* 地理の詳細項目 */}
                  <div className="ml-4 space-y-2">
                    <div className="text-xs text-muted-foreground">地理の詳細項目:</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { key: 'description', label: '概要' },
                        { key: 'climate', label: '気候' },
                        { key: 'terrain', label: '地形' },
                        { key: 'naturalResources', label: '天然資源' },
                        { key: 'notableLocations', label: '重要な場所' },
                        { key: 'dangerousZones', label: '危険地帯' }
                      ].map(item => {
                        const isLocked = typeof lockedSections.details?.geography === 'object' 
                          ? lockedSections.details.geography[item.key as keyof typeof lockedSections.details.geography]
                          : lockedSections.details?.geography
                        return (
                          <Button
                            key={item.key}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentGeography = typeof lockedSections.details?.geography === 'object' 
                                  ? lockedSections.details.geography 
                                  : {}
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  details: {
                                    ...lockedSections.details,
                                    geography: {
                                      ...currentGeography,
                                      [item.key]: !isLocked
                                    }
                                  }
                                })
                              }
                            }}
                            className="justify-start"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                            {item.label}
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              {/* システムタブ */}
              <TabsContent value="systems" className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'magicSystem', label: '魔法体系' },
                    { key: 'technology', label: '技術体系' }
                  ].map(item => {
                    const isLocked = lockedSections.details?.[item.key as keyof typeof lockedSections.details]
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            onLockedSectionsChange({
                              ...lockedSections,
                              details: {
                                ...lockedSections.details,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}
      
      <Tabs value={input.mode} onValueChange={handleModeChange}>
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 gap-1 h-auto">
          <TabsTrigger value="preset" className="text-xs sm:text-sm">プリセット</TabsTrigger>
          <TabsTrigger value="standard" className="text-xs sm:text-sm">カスタム</TabsTrigger>
          <TabsTrigger value="template" className="text-xs sm:text-sm">テンプレート</TabsTrigger>
          <TabsTrigger value="random" className="text-xs sm:text-sm">ランダム</TabsTrigger>
          <TabsTrigger value="idea" className="text-xs sm:text-sm">アイディア</TabsTrigger>
          <TabsTrigger value="reference" className="text-xs sm:text-sm">参照</TabsTrigger>
        </TabsList>
        
        <TabsContent value="preset" className="space-y-4">
          <PresetModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
        
        <TabsContent value="standard" className="space-y-4">
          <StandardModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
        
        <TabsContent value="template" className="space-y-4">
          <TemplateModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
        
        <TabsContent value="random" className="space-y-4">
          <RandomModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
        
        <TabsContent value="idea" className="space-y-4">
          <IdeaModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
        
        <TabsContent value="reference" className="space-y-4">
          <ReferenceModeForm
            input={input}
            onChange={onChange}
          />
        </TabsContent>
      </Tabs>
      
      {(input.mode === 'standard' || input.mode === 'template') && (
        <WorldParametersForm
          input={input}
          onChange={onChange}
        />
      )}
      
      {(input.mode === 'standard' || input.mode === 'template') && (
        <UniqueElementsForm
          input={input}
          onChange={onChange}
        />
      )}
      
      <GenerationOptionsForm
        input={input}
        onChange={onChange}
        useStreaming={useStreaming}
        onStreamingChange={onStreamingChange}
      />
      
      <div className="flex justify-end">
        <GenerateButton
          onClick={onGenerate}
          isLoading={isGenerating}
        />
      </div>
    </div>
  )
}